import { useEffect, useRef, useState } from "react";
import { IoMdClose } from "react-icons/io";
import {
  Button,
  TextField,
  CircularProgress,
  Collapse,
  Chip,
} from "@mui/material";
import {
  FaCloudUploadAlt,
  FaChevronDown,
  FaChevronRight,
  FaMoneyBillWave,
  FaBoxOpen,
  FaStore,
  FaTruck,
  FaBox,
  FaUniversity,
} from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  submitBatchPayment,
  SubmitBatchPaymentState,
  resetSubmitBatchPayment,
  selectSubmitBatchPayment,
} from "../slices/submit-batch-payment.slice";
import { dateSetup } from "features/stock-ordering/presentation/components/stock-ordering-utils";
import {
  GetProductDataRepository,
  GetProductDataResponse,
  GetOrderPaymentDetailsRepository,
  GetOrderPaymentDetailsRepoResponse,
} from "features/stock-ordering/data/stock-ordering.repository";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";

interface BatchPaymentModalProps {
  open: boolean;
  onClose: () => void;
  selectedOrders: any[];
}

interface SupplierPaymentInfo {
  bank_name: string | null;
  account_name: string | null;
  account_number: string | null;
  qr_code_image: string | null;
  payment_instructions: string | null;
}

export function BatchPaymentModal(props: BatchPaymentModalProps) {
  const { open, onClose, selectedOrders } = props;
  const dispatch = useAppDispatch();
  const submitBatchPaymentState = useAppSelector(selectSubmitBatchPayment);

  const mainDivRef = useRef<HTMLDivElement>(null);

  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [remarks, setRemarks] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());

  // Product data per order: { [orderId]: products[] }
  const [orderProducts, setOrderProducts] = useState<Record<string, any[]>>({});
  const [productsLoading, setProductsLoading] = useState(false);

  // Supplier payment info (fetched from first order)
  const [supplierPaymentInfo, setSupplierPaymentInfo] =
    useState<SupplierPaymentInfo | null>(null);
  const [supplierInfoLoading, setSupplierInfoLoading] = useState(false);

  // Fetch product data and supplier info when modal opens
  useEffect(() => {
    if (open && selectedOrders.length > 0) {
      fetchAllData();
    }
  }, [open]);

  const fetchAllData = async () => {
    // Fetch products for each order using the existing repository
    setProductsLoading(true);
    const productsMap: Record<string, any[]> = {};

    try {
      const productPromises = selectedOrders.map(async (order) => {
        try {
          const response: GetProductDataResponse =
            await GetProductDataRepository({
              orderId: order.id.toString(),
            });
          const data = response.data?.data?.product_data ?? [];
          productsMap[order.id.toString()] = data;
        } catch {
          productsMap[order.id.toString()] = [];
        }
      });

      await Promise.all(productPromises);
      setOrderProducts(productsMap);
    } catch {
      // Silently fail
    } finally {
      setProductsLoading(false);
    }

    // Fetch supplier payment info from the first order using existing repository
    setSupplierInfoLoading(true);
    try {
      const firstOrderId = selectedOrders[0].id;
      const response: GetOrderPaymentDetailsRepoResponse =
        await GetOrderPaymentDetailsRepository(Number(firstOrderId));
      const supplierInfo = response.data?.data?.supplier_payment_info ?? null;
      setSupplierPaymentInfo(supplierInfo);
    } catch {
      setSupplierPaymentInfo(null);
    } finally {
      setSupplierInfoLoading(false);
    }
  };

  // Handle successful submission
  useEffect(() => {
    if (submitBatchPaymentState.status === SubmitBatchPaymentState.success) {
      dispatch(resetSubmitBatchPayment());
      handleClose();
    }
  }, [submitBatchPaymentState.status]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleClose = () => {
    setPaymentProof(null);
    setRemarks("");
    setPreviewUrl(null);
    setExpandedOrders(new Set());
    setOrderProducts({});
    setSupplierPaymentInfo(null);
    dispatch(resetSubmitBatchPayment());
    document.body.classList.remove("overflow-hidden");
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, GIF, and PDF files are allowed");
        return;
      }

      setPaymentProof(file);

      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleSubmit = () => {
    if (!paymentProof || selectedOrders.length === 0) return;

    const orderIds = selectedOrders.map((order) => Number(order.id));

    dispatch(
      submitBatchPayment({
        orderIds,
        paymentProof,
        remarks: remarks || undefined,
      }),
    );
  };

  const toggleOrderExpanded = (orderId: number) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedOrders(new Set(selectedOrders.map((o) => Number(o.id))));
  };

  const collapseAll = () => {
    setExpandedOrders(new Set());
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(amount);
  };

  const totalAmount = selectedOrders.reduce(
    (sum, order) => sum + Number(order.total_cost || 0),
    0,
  );

  const isSubmitting =
    submitBatchPaymentState.status === SubmitBatchPaymentState.inProgress;

  if (!open) return null;

  if (open) {
    mainDivRef.current?.focus();
    document.body.classList.add("overflow-hidden");
  }

  return (
    <div
      ref={mainDivRef}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Escape") handleClose();
      }}
      className="fixed inset-0 z-30 flex items-start justify-center overflow-auto bg-black bg-opacity-30 backdrop-blur-sm"
    >
      <div className="w-[97%] lg:w-[900px] my-5 rounded-[10px]">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-[10px] flex items-center justify-between p-4">
          <div className="flex items-center space-x-3">
            <FaMoneyBillWave className="text-2xl text-white" />
            <div>
              <span className="text-xl text-white font-bold">
                BATCH PAYMENT
              </span>
              <p className="text-green-100 text-sm">
                {selectedOrders.length} orders selected
              </p>
            </div>
          </div>
          <button
            className="text-2xl text-white hover:text-gray-200"
            onClick={handleClose}
          >
            <IoMdClose />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 bg-paper border-b-2 border-l-2 border-r-2 border-green-600 space-y-5 select-none rounded-b-[10px]">
          {/* Summary Card */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Amount</p>
                <p className="text-3xl font-bold text-green-700">
                  {formatCurrency(totalAmount)}
                </p>
              </div>
              <div className="text-right">
                <Chip
                  label={`${selectedOrders.length} Orders`}
                  color="success"
                  variant="outlined"
                  icon={<FaBoxOpen />}
                />
              </div>
            </div>
          </div>

          {/* Supplier Payment Information */}
          <div className="border rounded-2xl shadow-sm p-5 bg-white">
            <div className="flex items-center space-x-2 text-lg font-semibold text-gray-800 mb-4">
              <FaUniversity className="text-green-600" />
              <span>Payment Information</span>
            </div>

            {supplierInfoLoading ? (
              <div className="flex justify-center py-4">
                <CircularProgress size={24} sx={{ color: "#16a34a" }} />
              </div>
            ) : supplierPaymentInfo ? (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-md font-semibold text-green-800 mb-4">
                  Supplier Bank Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    {supplierPaymentInfo.bank_name && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">
                          Bank Name
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {supplierPaymentInfo.bank_name}
                        </p>
                      </div>
                    )}

                    {supplierPaymentInfo.account_name && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">
                          Account Name
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {supplierPaymentInfo.account_name}
                        </p>
                      </div>
                    )}

                    {supplierPaymentInfo.account_number && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">
                          Account Number
                        </p>
                        <p className="text-gray-900 font-semibold font-mono text-lg">
                          {supplierPaymentInfo.account_number}
                        </p>
                      </div>
                    )}

                    {supplierPaymentInfo.payment_instructions && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">
                          Instructions
                        </p>
                        <p className="text-gray-700 text-sm">
                          {supplierPaymentInfo.payment_instructions}
                        </p>
                      </div>
                    )}
                  </div>

                  {supplierPaymentInfo.qr_code_image && (
                    <div className="flex flex-col items-center justify-center">
                      <div className="bg-white p-4 rounded-lg shadow-md">
                        <img
                          src={`${REACT_APP_DOMAIN_URL}assets/uploads/qr-codes/${supplierPaymentInfo.qr_code_image}`}
                          alt="Payment QR Code"
                          className="max-w-[200px] max-h-[200px] object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Scan to pay via GCash/Maya
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                <p className="text-yellow-800 text-sm">
                  Supplier payment details are not available. Please contact the
                  supplier directly for payment instructions.
                </p>
              </div>
            )}
          </div>

          {/* Order List with Products */}
          <div className="border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-800 flex items-center space-x-2">
                <FaBox className="text-green-600" />
                <span>Order Details</span>
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={expandAll}
                  className="text-xs text-green-600 hover:text-green-800 font-medium"
                >
                  Expand All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={collapseAll}
                  className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                >
                  Collapse All
                </button>
              </div>
            </div>

            <div className="divide-y">
              {selectedOrders.map((order) => {
                const isExpanded = expandedOrders.has(Number(order.id));
                const products = orderProducts[order.id.toString()] ?? [];
                const orderTotal = products.reduce(
                  (sum, p) =>
                    sum +
                    (p.commited_qty || p.order_qty) * (p.productCost || 0),
                  0,
                );

                return (
                  <div key={order.id}>
                    {/* Accordion Header */}
                    <button
                      onClick={() => toggleOrderExpanded(Number(order.id))}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center space-x-3">
                        {isExpanded ? (
                          <FaChevronDown className="text-gray-400 text-sm flex-shrink-0" />
                        ) : (
                          <FaChevronRight className="text-gray-400 text-sm flex-shrink-0" />
                        )}
                        <div>
                          <p className="font-semibold text-gray-800">
                            {order.po_number || `ORD-${order.id}`}
                          </p>
                          <p className="text-sm text-gray-500">
                            {order.store_name}
                            {order.actual_delivery_date && (
                              <span className="text-gray-400">
                                {" · Delivered "}
                                {dateSetup(order.actual_delivery_date, false)}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="font-bold text-green-700">
                          {formatCurrency(Number(order.total_cost))}
                        </span>
                        {products.length > 0 && (
                          <p className="text-xs text-gray-400">
                            {products.length} item
                            {products.length > 1 ? "s" : ""}
                          </p>
                        )}
                      </div>
                    </button>

                    {/* Accordion Content */}
                    <Collapse in={isExpanded}>
                      <div className="px-4 pb-4 pl-12 space-y-3">
                        {/* Order Info Grid */}
                        <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="flex items-center space-x-2">
                            <FaStore className="text-gray-400 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500">Store</p>
                              <p className="font-medium">{order.store_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <FaTruck className="text-gray-400 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500">Supplier</p>
                              <p className="font-medium">
                                {order.supplier_name}
                              </p>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">
                              Delivery Date
                            </p>
                            <p className="font-medium">
                              {order.actual_delivery_date
                                ? dateSetup(order.actual_delivery_date, false)
                                : "N/A"}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Amount</p>
                            <p className="font-medium text-green-700">
                              {formatCurrency(Number(order.total_cost))}
                            </p>
                          </div>
                        </div>

                        {/* Product Table */}
                        {productsLoading ? (
                          <div className="flex justify-center py-3">
                            <CircularProgress
                              size={20}
                              sx={{ color: "#16a34a" }}
                            />
                          </div>
                        ) : products.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    Product
                                  </th>
                                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                    Qty
                                  </th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                    Unit Price
                                  </th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                    Total
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {products.map((product, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-3 py-2">
                                      <span className="font-medium text-gray-900">
                                        {product.product_name}
                                      </span>
                                      {product.product_id && (
                                        <span className="text-xs text-gray-400 ml-1">
                                          ({product.product_id})
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-center text-gray-700">
                                      {product.commited_qty ||
                                        product.order_qty}
                                      {product.uom && (
                                        <span className="text-xs text-gray-400 ml-1">
                                          {product.uom}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-700">
                                      {formatCurrency(product.productCost || 0)}
                                    </td>
                                    <td className="px-3 py-2 text-right font-medium text-gray-900">
                                      {formatCurrency(
                                        (product.commited_qty ||
                                          product.order_qty) *
                                          (product.productCost || 0),
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-50">
                                <tr>
                                  <td
                                    colSpan={3}
                                    className="px-3 py-2 text-right text-xs font-semibold text-gray-600"
                                  >
                                    Subtotal:
                                  </td>
                                  <td className="px-3 py-2 text-right font-bold text-green-700">
                                    {formatCurrency(orderTotal)}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">
                            No product data available.
                          </p>
                        )}
                      </div>
                    </Collapse>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upload Payment Proof */}
          <div className="border rounded-xl p-5 bg-white space-y-4">
            <h3 className="text-md font-semibold text-gray-800 flex items-center space-x-2">
              <FaCloudUploadAlt className="text-green-600" />
              <span>Upload Payment Proof</span>
              <span className="text-red-500">*</span>
            </h3>

            <p className="text-sm text-gray-500">
              This receipt will be applied to all {selectedOrders.length}{" "}
              selected orders.
            </p>

            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                paymentProof
                  ? "border-green-400 bg-green-50"
                  : "border-gray-300 hover:border-green-500"
              }`}
            >
              <input
                type="file"
                id="batchPaymentProof"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="batchPaymentProof"
                className="cursor-pointer flex flex-col items-center"
              >
                {previewUrl ? (
                  <div className="space-y-3">
                    <img
                      src={previewUrl}
                      alt="Payment proof preview"
                      className="max-w-[200px] max-h-[200px] object-contain rounded-lg shadow-md"
                    />
                    <p className="text-sm text-green-600 font-medium">
                      {paymentProof?.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Click to change file
                    </p>
                  </div>
                ) : paymentProof ? (
                  <div className="space-y-3">
                    <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="text-3xl">📄</span>
                    </div>
                    <p className="text-sm text-green-600 font-medium">
                      {paymentProof.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Click to change file
                    </p>
                  </div>
                ) : (
                  <>
                    <FaCloudUploadAlt className="text-4xl text-gray-400 mb-3" />
                    <p className="text-gray-600 font-medium">
                      Drop payment screenshot here or click to upload
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Accepted: JPG, PNG, GIF, PDF (Max 5MB)
                    </p>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Remarks */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Remarks (Optional)
            </label>
            <TextField
              multiline
              rows={3}
              fullWidth
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any notes about this batch payment (e.g., reference number, payment method)..."
              variant="outlined"
              size="small"
            />
          </div>

          {/* Error Message */}
          {submitBatchPaymentState.status === SubmitBatchPaymentState.fail && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {submitBatchPaymentState.message ||
                "Failed to submit batch payment. Please try again."}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-center pt-4 border-t gap-3">
            <div className="text-sm text-gray-500">
              Total:{" "}
              <span className="font-bold text-green-700 text-lg">
                {formatCurrency(totalAmount)}
              </span>{" "}
              for {selectedOrders.length} orders
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isSubmitting}
                sx={{ color: "gray", borderColor: "gray" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!paymentProof || isSubmitting}
                sx={{
                  backgroundColor: "#16a34a",
                  "&:hover": { backgroundColor: "#15803d" },
                }}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <FaMoneyBillWave />
                  )
                }
              >
                {isSubmitting
                  ? "Submitting..."
                  : `Submit Payment for ${selectedOrders.length} Orders`}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
